import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronIcon, TrashIcon } from '@/components/catalog/icons';
import {
  CreditNoteAllocationsPanel,
  XeroOutcome,
} from '@/components/credit-notes/CreditNoteAllocationsPanel';
import { CreditNoteLineItemsTable } from '@/components/credit-notes/CreditNoteLineItemsTable';
import { DownloadPdfButton } from '@/components/documents/DownloadPdfButton';
import { ApiError, api } from '@/lib/api';
/*
 * ⛔ **EVERY SENTENCE THIS PAGE PUTS IN FRONT OF A DECISION ABOUT MONEY COMES
 * FROM `@/lib/credit-notes`, AND NOTHING HERE RESPELLS ONE.** Two copies of a
 * consequence agree the day they are written and drift on the first correction
 * — and what would drift is the sentence somebody reads immediately before
 * making money spendable, withdrawing it, or destroying the record of it. The
 * list page, the company panel and this one must describe approve, void and
 * delete identically, or one of them is lying.
 *
 * ⚠️ The LABELS are imported for the same reason and are not retyped:
 * `CREDIT_AVAILABLE_LABEL` and `CREDIT_REMAINING_LABEL` are one word apart,
 * swapping them COMPILES, and the module's own rule is that the first may only
 * ever sit over a figure that passed `spendableOf`. See the Totals panel.
 */
import {
  CREDIT_ALLOCATED_LABEL,
  CREDIT_AVAILABLE_LABEL,
  CREDIT_REMAINING_LABEL,
  allocationPushesOutcome,
  approvalConsequence,
  CREDIT_NOTE_NOT_SENT_NOTE,
  creditNotePushOutcome,
  creditNoteRef,
  deleteRefusal,
  editableOf,
  spendableOf,
  voidWarning,
  type XeroOutcomeLine,
} from '@/lib/credit-notes';
import { formatDateOnly, formatDateTime } from '@/lib/format';
import { documentCustomerName } from '@/lib/company-names';
import { Money as UiMoney } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { denialReason } from '@/lib/permissions';
import type { CreditNote, CreditNoteAllocation } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
} from '@/components/ui';

/*
 * The permission module this screen answers to.
 *
 * ✅ **`approve` IS expressible — it is the FIFTH action, catalogued alongside
 * `convert`, and `ability.canApprove('credit-notes')` is the question to ask.**
 * So the Approve control below is gated like every other control here rather
 * than rendered blind; the render-it-and-let-the-403-arrive fallback that would
 * apply if the model could not ask is not needed and is not used.
 *
 * ⛔ **NEVER gate Approve on `isAdmin`, and never borrow `update` for it.** It
 * is a GRANT, not a tier, and it is a separation of duties: `accounts` holds
 * read/create/update and deliberately NOT `approve`, so the person who raises a
 * credit note is not the person who makes its money spendable. Borrowing
 * `update` would be the `integrations`-borrowing-`companies` mistake again — a
 * tickbox honest about the wrong thing, silently offering approval to everybody
 * who may edit — and a role check would disagree with the server the moment a
 * super-admin moved the grant to another set.
 *
 * ⚠️ **The gate is the optimisation; the 403 is the guarantee.** The server
 * enforces the grant, and a role or a grant can be taken away under a live
 * access token, which lasts days — so every one of the three writes still
 * handles its refusal. The 403 lands in the dialog that asked for it, and in the
 * panel-level banner below once that dialog is gone — never on a field, because
 * a 403 body carries no `details` and there is nothing to bind.
 */
const CREDIT_NOTE_MODULE = 'credit-notes';

/*
 * ⚠️ **TWO STATES `@/lib/credit-notes` DOES NOT COVER, AND THEY ARE NOT
 * RESPELLINGS OF ANYTHING IT SAYS.** `voidWarning` and `deleteRefusal` are both
 * answered FROM THE ALLOCATIONS, so neither can be asked until that read lands —
 * and it can fail on its own, independently of the note.
 *
 * ⛔ **Passing `[]` for an unread list is the one thing that must not happen.**
 * Both functions read an empty list as *nothing is holding this note down*, so
 * an unknown count would be presented as a known zero: the void dialog would
 * drop its refusal branch and the delete dialog would stop naming what to
 * un-apply first. That is wrong in the reassuring direction, on the two controls
 * where the reassuring direction costs somebody their record.
 */
const VOID_WITH_ALLOCATIONS_UNKNOWN =
  'The allocations on this credit note could not be read, so what voiding it withdraws cannot be listed here. The server refuses a void while allocations stand.';
const DELETE_WITH_ALLOCATIONS_UNKNOWN =
  'The allocations on this credit note could not be read. Deleting it is refused while any allocation stands, and the server names how many.';

/* --- small shared bits --------------------------------------------------- */

function BackToCreditNotes() {
  return (
    <Link
      to="/credit-notes"
      className="inline-flex items-center gap-1 rounded-sm text-xs text-fg-muted transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
    >
      <ChevronIcon direction="left" />
      All credit notes
    </Link>
  );
}

function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function DetailGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <PanelBody className="border-b border-border last:border-b-0">
      <h3 className="mb-3 text-sm font-semibold text-fg">{title}</h3>
      {/*
        One column below `sm`, which is what keeps this readable at 320px: every
        label and figure is its own row there, rather than a two-up grid
        squeezing a currency code off the end of an amount.
      */}
      <dl className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">{children}</dl>
    </PanelBody>
  );
}

/*
 * ⛔ **Money is an exact decimal STRING and never sees `Number()`** — a credit
 * note's whole job is to reconcile against an invoice, so a float rounding half
 * a penny differently from the database is a balance that does not close.
 *
 * ⚠️ The currency code always travels with the figure. `currency` is `CHAR(3)`,
 * never null on a credit note, and nothing anywhere converts it — a bare figure
 * invites somebody to add it to one in another currency.
 */
function Money({
  value,
  currency,
  emptyLabel = 'Not recorded',
}: {
  value: string | null;
  currency: string;
  emptyLabel?: string;
}) {
  /*
   * `discountTotal` and `taxTotal` are nullable, and `null` is *the document
   * never said* — a different fact from a recorded zero. Never `?? '0.00'`.
   */
  if (value === null) return <span className="text-fg-subtle">{emptyLabel}</span>;
  return <UiMoney amount={value} currency={currency} />;
}

/**
 * The status, as a badge.
 *
 * ⛔ **Read the FLAGS, never the name.** `status.name` is admin-editable free
 * text and is a label for a person; this codebase has been bitten four times by
 * matching on one (`"Unpaid"` contains `"paid"`, `"Not Accepted"` contains
 * `"accept"`, a product rename switched campaign inclusions off, an inclusion
 * was renamed under a matcher). The name is RENDERED here and matched on
 * nowhere; those are different things and only the second is forbidden.
 *
 * ⚠️ **The colour is never the only signal** — the status's own name is the
 * text, so a reader who cannot tell two tones apart still reads the state.
 *
 * ⛔ **`isApproved` earns no tone of its own.** It stays `true` after a void, so
 * an "approved" green on a withdrawn note would paint dead money as usable.
 * `spendable` is the only thing that earns the success tone.
 */
function StatusBadge({ status, spendable }: { status: CreditNote['status']; spendable: boolean }) {
  const tone = status.isVoid
    ? /*
       * `warning`, not `danger`: voiding is a deliberate withdrawal, not a fault
       * in the record, and a red badge would send somebody hunting a problem. It
       * is not `neutral` either — a voided note must not sit at the same weight
       * as a draft, because one is worth nothing and the other is not worth
       * anything YET.
       */
      ('warning' as const)
    : spendable
      ? ('success' as const)
      : status.isAwaitingApproval
        ? ('info' as const)
        : ('neutral' as const);
  return <Badge tone={tone}>{status.name}</Badge>;
}

/* --- loading shape ------------------------------------------------------- */

/*
 * Skeletons in the REAL shape rather than a spinner, so the page settles into
 * the layout it is already drawing and nothing jumps when the record lands.
 */
function DetailsSkeleton() {
  return (
    <div className="grid gap-4">
      <Panel className="min-w-0">
        <PanelHeader title="Credit note details" />
        <PanelBody>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="grid gap-1.5">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-3.5 w-32" />
              </div>
            ))}
          </div>
        </PanelBody>
      </Panel>
      <Panel className="min-w-0">
        <PanelHeader title="Totals" />
        <PanelBody>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 7 }, (_, i) => (
              <div key={i} className="grid gap-1.5">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-3.5 w-28" />
              </div>
            ))}
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

type LoadResult =
  | { kind: 'ready'; creditNote: CreditNote }
  | { kind: 'notfound' }
  | { kind: 'error'; message: string };

export function CreditNoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { ability } = useAuth();

  /*
   * The read gate, computed here because the return that uses it has to sit
   * below every hook.
   */
  const canReadModule = ability.canRead(CREDIT_NOTE_MODULE);

  /*
   * A non-numeric path segment can never match a record, so it is a 404 without
   * asking the API — which would answer 422 for it anyway, and a validation
   * error is not what "/credit-notes/banana" means to a person.
   */
  const creditNoteId = id !== undefined && /^\d+$/.test(id) ? Number(id) : null;

  /*
   * One id-stamped slot rather than separate loading / error / notFound flags:
   * navigating straight from one credit note to another can never flash the
   * previous record's BALANCE under the new id, and "loading" is simply the
   * absence of a result for this id. On a money screen that is not tidiness — a
   * stale remaining figure under a new heading is a wrong answer about how much
   * a customer is owed.
   */
  const [result, setResult] = useState<{ id: number; value: LoadResult } | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  /*
   * The allocations, read HERE as well as by the panel below.
   *
   * ⚠️ **Not a duplicate to be tidied away.** Both destructive confirmations are
   * worded FROM this list — `voidWarning` and `deleteRefusal` both take it — so
   * the page needs it in hand before it can say what voiding withdraws or what
   * is holding a delete down. The panel does its own read regardless; that read
   * answers *what may be allocated*, this one answers *what may this page
   * promise*.
   *
   * `null` is UNKNOWN — unread or failed — and is never folded into `[]`. See
   * the two constants at the top of this file.
   */
  const [allocations, setAllocations] = useState<{
    id: number;
    rows: readonly CreditNoteAllocation[] | null;
  } | null>(null);

  const [pendingApprove, setPendingApprove] = useState(false);
  /* Draft → the awaiting-approval status (migration 2026-10-16, owner decision 23). */
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [pendingVoid, setPendingVoid] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  /*
   * ⚠️ **Panel level, never per field.** A 403 body carries no `details`, so
   * there is nothing to bind to a control; and the 409 on delete is about the
   * record's relationships rather than about anything on this screen.
   */
  const [actionError, setActionError] = useState<string | null>(null);

  /*
   * What the last approve or void did in XERO (2026-10-03: both now reach it).
   * Separate from `actionError` because the action itself SUCCEEDED here — a
   * refused push is not a refused approval, and wording it as one would send
   * somebody to approve the note a second time.
   */
  // Id-stamped like `result`, so navigating to another note cannot show this one's report.
  const [xeroOutcomes, setXeroOutcomes] = useState<{
    id: number;
    lines: XeroOutcomeLine[];
  } | null>(null);
  /*
   * Bumped after approve: the server pushes every allocation not yet in Xero
   * then, and the allocations panel re-reads its own list only on its own
   * writes — so without this its Xero column would go on saying "Not in Xero"
   * over allocations that just landed.
   */
  const [allocationsPanelReload, setAllocationsPanelReload] = useState(0);

  const canUpdate = ability.canUpdate(CREDIT_NOTE_MODULE);
  const canDelete = ability.canDelete(CREDIT_NOTE_MODULE);
  /*
   * ⛔ **`canApprove`, never `canUpdate` and never `isAdmin`.** The three agree
   * on most accounts today and that is a coincidence of how the sets happen to
   * be granted, not a rule — `accounts` holds `update` and not `approve`
   * precisely so that raising a credit note and making its money spendable are
   * two people. The parameter is narrowed to this one module by the ability
   * type, so this call cannot drift onto a module that has no such action.
   */
  const canApprove = ability.canApprove(CREDIT_NOTE_MODULE);

  /*
   * Per-person section layout (owner 2026-10-20). Called above every early
   * return so the hook order never changes. Ids are stored per user: never
   * rename one. The defaults are today's stack, top to bottom, all full width.
   */
  const layout = usePageLayout('credit-note', [
    { id: 'details', label: 'Credit note details' },
    { id: 'totals', label: 'Totals' },
    { id: 'lines', label: 'Line items' },
    { id: 'allocations', label: 'Applied to invoices' },
  ]);

  const loadCreditNote = useCallback(
    (signal?: AbortSignal) => {
      if (creditNoteId === null) return Promise.resolve();
      const settle = (value: LoadResult) => setResult({ id: creditNoteId, value });
      return api
        .getCreditNote(creditNoteId, signal)
        .then((response) => settle({ kind: 'ready', creditNote: response.data }))
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          if (caught instanceof ApiError && caught.status === 404) {
            settle({ kind: 'notfound' });
            return;
          }
          settle({
            kind: 'error',
            message: caught instanceof Error ? caught.message : 'Failed to load this credit note',
          });
        });
    },
    [creditNoteId],
  );

  const loadAllocations = useCallback(
    (signal?: AbortSignal) => {
      if (creditNoteId === null) return Promise.resolve();
      return api
        .listCreditNoteAllocations(creditNoteId, signal)
        .then((response) => setAllocations({ id: creditNoteId, rows: response.data }))
        .catch(() => {
          if (signal?.aborted) return;
          /*
           * A failed read must stay UNKNOWN. The header — where the money
           * everybody came for actually lives — still renders; only the two
           * confirmations degrade, and they say so.
           */
          setAllocations({ id: creditNoteId, rows: null });
        });
    },
    [creditNoteId],
  );

  useEffect(() => {
    if (creditNoteId === null) return;
    const controller = new AbortController();
    void loadCreditNote(controller.signal);
    void loadAllocations(controller.signal);
    return () => controller.abort();
  }, [creditNoteId, loadCreditNote, loadAllocations, reloadToken]);

  const current = result && result.id === creditNoteId ? result.value : null;
  const allocationsFor = allocations && allocations.id === creditNoteId ? allocations.rows : null;

  /*
   * ⚠️ **NOT OPTIONAL, and not a refresh for tidiness.** Every line write and
   * every allocation rewrites this note's header totals SERVER-SIDE, in the same
   * transaction, and neither child returns the new header — so `grandTotal`,
   * `allocatedTotal` and `remainingAmount` on screen are stale the instant
   * either succeeds. All three are money, and the staleness is completely
   * silent: no error, no empty state, just a balance that no longer reconciles.
   *
   * This is what `onChanged` is for on both children, and why it goes back to
   * the server rather than patching a local copy. The allocations go with it,
   * because they are what the two confirmations below are worded from.
   */
  const refetch = useCallback(async () => {
    await Promise.all([loadCreditNote(), loadAllocations()]);
  }, [loadCreditNote, loadAllocations]);

  /*
   * ⚠️ **Approve and void RENDER WHAT COMES BACK rather than re-reading or
   * patching.** Both endpoints resolve to the WHOLE record, because the status,
   * the approver and `spendable` move together in one server-side transaction —
   * a client that set the status itself would be guessing at the other two, and
   * the failure is a screen offering to spend a note the server will refuse, or
   * withholding one it would have allowed. Neither reports anything.
   */
  function settleFromWrite(saved: CreditNote) {
    setResult({ id: saved.id, value: { kind: 'ready', creditNote: saved } });
  }

  /*
   * One shape for all three writes, so the refusal stays in the dialog that
   * asked for it — beside the decision, rather than somewhere the reader has to
   * go looking.
   */
  async function runAction(run: () => Promise<void>, deniedFallback: string) {
    setBusy(true);
    setActionError(null);
    try {
      await run();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        /*
         * ⚠️ The gate on the control is an optimisation and this is the
         * guarantee: a role or a grant can be taken away under a live access
         * token, which lasts days. And for APPROVE there is no gate at all — the
         * ability model cannot express the action — so this branch is the only
         * thing between a reader and a silent failure.
         */
        setActionError(deniedFallback);
      } else if (caught instanceof ApiError && caught.status === 404) {
        setActionError('This credit note no longer exists.');
      } else {
        /*
         * ⚠️ **Verbatim.** The 409 on delete NAMES THE COUNT of allocations
         * holding the note down (the foreign key is RESTRICT), and that count is
         * the whole actionable half — a house sentence in its place tells
         * somebody the delete failed and nothing about what to unpick first.
         */
        setActionError(caught instanceof Error ? caught.message : 'That didn’t work.');
      }
    } finally {
      setBusy(false);
    }
  }

  /*
   * The read gate, below every hook so the early return cannot change the hook
   * order — and ABOVE the not-found and error branches, deliberately. A reader
   * who may not see credit notes must not be told whether this one exists:
   * "not found" over a record that is merely hidden from them is a wrong answer
   * dressed as a real one.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. And hiding a page is
   * not refusing a request, so nothing here may be worded as though the record
   * were protected.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Accounts" title="Credit notes" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  if (creditNoteId === null) {
    return (
      <>
        <PageHeader eyebrow={<BackToCreditNotes />} title="Credit note not found" />
        <Panel className="min-w-0">
          <EmptyState
            title="That isn’t a credit note reference"
            description="A credit note id is a positive whole number."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/credit-notes')}>
                Back to credit notes
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (current === null) {
    return (
      <>
        <PageHeader eyebrow={<BackToCreditNotes />} title="Credit note" />
        <DetailsSkeleton />
      </>
    );
  }

  if (current.kind === 'notfound') {
    return (
      <>
        <PageHeader eyebrow={<BackToCreditNotes />} title="Credit note not found" />
        <Panel className="min-w-0">
          <EmptyState
            title="This credit note doesn’t exist"
            description="It may have been deleted by someone else since this link was made."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/credit-notes')}>
                Back to credit notes
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (current.kind === 'error') {
    return (
      <>
        <PageHeader eyebrow={<BackToCreditNotes />} title="Credit note" />
        <Panel className="min-w-0">
          <PanelBody className="space-y-3">
            <Notice tone="danger" role="alert" title="Couldn’t load this credit note.">
              <span className="[overflow-wrap:anywhere]">{current.message}</span>
            </Notice>
            <Button variant="secondary" size="sm" onClick={() => setReloadToken((n) => n + 1)}>
              Try again
            </Button>
          </PanelBody>
        </Panel>
      </>
    );
  }

  const { creditNote } = current;

  /*
   * ⛔ **NOT re-derived here, and the reason is the module's own.** Editability
   * is the status flag AND the note not already being in Xero (owner decision
   * 18); `editableOf` owns both halves so that this page and the line grid
   * cannot disagree about which document may be changed. ⚠️ *SUPERSEDED: "Its
   * Xero half is a declared, documented GAP in that module — do not "complete"
   * it at this call site".* The gap is closed: credit-note reads carry
   * `inXero` and `editableOf` refuses on it. Still do not re-test Xero at this
   * call site, which is how two screens come to answer differently.
   */
  const editVerdict = editableOf(creditNote);

  /*
   * ⛔ **Read `spendableOf`, never `status.isApproved`.** The two agree until
   * somebody voids an approved note, at which point the flag stays `true` and
   * this goes `false`; a control gated on the flag then spends withdrawn money
   * with a `200` and nothing on screen looking wrong.
   */
  const spendable = spendableOf(creditNote);

  /*
   * 🔴 **`isApproved` AND `isVoid` ARE NOT COMPLEMENTS — a note is approved and
   * THEN voided, and both flags are true on that row.** So Approve is offered on
   * a note that has never been approved, and Void on anything not already
   * withdrawn; neither is the inverse of the other, and neither is derived from
   * `spendable`, which answers a third question again.
   */
  const canBeApproved = !creditNote.status.isApproved && !creditNote.status.isVoid;
  const canBeVoided = !creditNote.status.isVoid;

  /*
   * `null` where the allocations could not be read. ⚠️ `deleteRefusal` cannot be
   * asked at all in that state — it reads the list — so the delete confirm is
   * left ENABLED and the 409 is allowed to arrive. Holding the button down on an
   * unknown would refuse a delete the server might well have allowed.
   */
  const deleteBlocked =
    allocationsFor === null ? null : deleteRefusal(creditNote, allocationsFor);

  // Blank subject still has to identify the record in a header and in three dialog titles.
  const title = creditNote.subject.trim() || `Credit note #${creditNote.id}`;

  return (
    <>
      <PageHeader
        eyebrow={<BackToCreditNotes />}
        title={title}
        description={
          <>
            <StatusBadge status={creditNote.status} spendable={spendable} />
            {/*
              ⛔ **PROVENANCE, NEVER A SYNC STATE.** `importedFromXero` says this
              note was RAISED in Xero and imported here — it says nothing
              whatever about whether it is currently linked, in step, or reaching
              Xero at all. Wording it as a connection is the `xeroQuoteNumber`
              proxy-field mistake one field along: a fact written to answer one
              question, read as the answer to another.
            */}
            {creditNote.importedFromXero && (
              <Badge tone="neutral" variant="outline">
                Imported from Xero
              </Badge>
            )}
            <span className="text-fg-subtle">·</span>
            <span className="tabular">#{creditNote.id}</span>
            {/*
              EMERGE's own id leads, bare, because it is the id and is always
              present. Xero's number follows and is NAMED, because it belongs to
              another system and is absent until a push lands. ⚠️ A populated
              number is not proof this note is in Xero — it records the number of
              the last push that SUCCEEDED — so it is printed as a reference and
              nothing on this page tests it.
            */}
            {creditNote.xeroCreditNoteNumber && (
              <>
                <span className="text-fg-subtle">·</span>
                <span className="tabular">Xero {creditNote.xeroCreditNoteNumber}</span>
              </>
            )}
            <span className="text-fg-subtle">·</span>
            {/* `company` is `NOT NULL` on a credit note, unlike an invoice's — no guard, and a `?? '—'` beside it would be dead code reading as a real empty state. */}
            <TextLink to={`/companies/${creditNote.company.id}`}>{documentCustomerName(creditNote.company)}</TextLink>
          </>
        }
        actions={
          <>
            {/*
              Gated on `credit-notes.read` server-side — the same key this page
              already needed to load — so there is no ability test here. It
              renders NOTHING where the note is provably not in Xero; see
              `canDownloadCreditNotePdf`, which reads no Xero number column. The
              reference is `creditNoteRef` (Xero's number, else the subject),
              never the id — it also names the fallback file.
            */}
            <DownloadPdfButton
              kind="credit-note"
              document={creditNote}
              reference={creditNoteRef(creditNote)}
              variant="detail"
            />
            {/*
              ⚠️ **TWO independent tests, and neither implies the other**: the
              record must be in a state that can be approved, AND this reader
              must hold the separate `approve` grant. A note awaiting approval is
              still not approvable BY a reader from the Accounts set, and a
              reader who holds the grant still cannot approve a voided note.
            */}
            {/*
              Submit for approval: offered on an EDITABLE status only (the status
              flag, never its name), to anyone who may update credit notes. The
              server moves the note to its awaiting-approval status and emails the
              approvers; editing stops until it is approved or voided.
            */}
            {creditNote.status.isEditable && !creditNote.status.isVoid && canUpdate && (
              <Button
                variant={canBeApproved && canApprove ? 'secondary' : 'primary'}
                onClick={() => {
                  setActionError(null);
                  setPendingSubmit(true);
                }}
              >
                Submit for approval
              </Button>
            )}
            {canBeApproved && canApprove && (
              <Button
                variant="primary"
                onClick={() => {
                  setActionError(null);
                  setPendingApprove(true);
                }}
              >
                Approve
              </Button>
            )}
            {canBeVoided && canUpdate && (
              <Button
                variant="secondary"
                onClick={() => {
                  setActionError(null);
                  setPendingVoid(true);
                }}
              >
                Void
              </Button>
            )}
            {canDelete && (
              <Button
                variant="danger"
                iconOnly
                leadingIcon={<TrashIcon />}
                aria-label={`Delete ${title}`}
                title={`Delete ${title}`}
                onClick={() => {
                  setActionError(null);
                  setPendingDelete(true);
                }}
              />
            )}
            <LayoutToolbar layout={layout} />
          </>
        }
      />

      {/*
        ⚠️ `min-w-0` on every panel in this grid is load-bearing: a grid item
        defaults to min-width:auto, so the lines table would widen the whole
        track instead of scrolling inside its own wrapper, overflowing the page
        sideways below 768px.
      */}
      <div className="grid gap-4">
        {/*
          The refusal that outlived its dialog — a 403 on approve, say, once the
          person has dismissed the confirmation. Panel level, because a 403
          carries no `details` and there is no field to hang it on.
        */}
        {actionError !== null &&
          !pendingApprove &&
          !pendingSubmit &&
          !pendingVoid &&
          !pendingDelete && (
          <Notice tone="danger" role="alert" title="That didn’t go through." className="min-w-0">
            <span className="[overflow-wrap:anywhere]">{actionError}</span>
          </Notice>
        )}

        {/*
          EXCEPTION 4 — what the last approve or void did in Xero. Not inside the
          red banner above: the action succeeded here, and only a `danger` line
          is a refusal. A skip is ordinary and is toned as one.
        */}
        {xeroOutcomes !== null &&
          xeroOutcomes.id === creditNote.id &&
          xeroOutcomes.lines.length > 0 && (
            <Panel className="min-w-0">
              <PanelBody className="space-y-1">
                {xeroOutcomes.lines.map((line) => (
                  <XeroOutcome key={`${line.tone}:${line.text}`} line={line} />
                ))}
              </PanelBody>
            </Panel>
          )}

        <LayoutGrid
          layout={layout}
          render={{
            details: () => (
        <Panel className="min-w-0" aria-labelledby="credit-note-details-heading">
          <PanelHeader id="credit-note-details-heading" title="Credit note details" />

          <DetailGroup title="Why this was raised">
            {/* `creditReason` is `NOT NULL` — a credit note with no stated reason is not expressible, which is the point: this is the field an auditor reads. */}
            <DetailItem label="Reason">{creditNote.creditReason.name}</DetailItem>
            <DetailItem label="Reason detail">
              {creditNote.creditReasonDetail ?? (
                <span className="text-fg-subtle">The reason above said enough</span>
              )}
            </DetailItem>
            {/* Plain `YYYY-MM-DD`; `formatDateOnly` reads it as calendar parts, never through `new Date()`, which renders a day early west of Greenwich. */}
            <DetailItem label="Date">
              {creditNote.creditNoteDate === null ? (
                <span className="text-fg-subtle">Not recorded</span>
              ) : (
                formatDateOnly(creditNote.creditNoteDate)
              )}
            </DetailItem>
            <DetailItem label="Currency">
              <span className="tabular">{creditNote.currency}</span>
            </DetailItem>
            {/* ⚠️ The CUSTOMER's reference, never ours and never Xero's — the heading says so, rather than leaving it to be read as a number of ours. */}
            <DetailItem label="Customer reference">
              {creditNote.reference ?? <span className="text-fg-subtle">None given</span>}
            </DetailItem>
          </DetailGroup>

          {/*
            ⚠️ **The approval is shown whether or not the note is still
            spendable, and voiding does NOT clear it.** The approval happened and
            the audit trail says so; hiding it on a voided note would erase who
            made the decision that is now being asked about.
          */}
          <DetailGroup title="Approval">
            <DetailItem label="Approved by">
              {creditNote.approvedBy?.name ?? <span className="text-fg-subtle">Not approved</span>}
            </DetailItem>
            <DetailItem label="Approved at">
              {creditNote.approvedAt === null ? (
                <span className="text-fg-subtle">Not approved</span>
              ) : (
                formatDateTime(creditNote.approvedAt)
              )}
            </DetailItem>
          </DetailGroup>
        </Panel>
            ),
            totals: () => (
        <Panel className="min-w-0" aria-labelledby="credit-note-totals-heading">
          <PanelHeader id="credit-note-totals-heading" title="Totals" />
          <DetailGroup title="This credit note">
            <DetailItem label="Sub total">
              <Money value={creditNote.subTotal} currency={creditNote.currency} />
            </DetailItem>
            <DetailItem label="Discount total">
              <Money
                value={creditNote.discountTotal}
                currency={creditNote.currency}
                emptyLabel="Not recorded on this note"
              />
            </DetailItem>
            <DetailItem label="Tax total">
              <Money
                value={creditNote.taxTotal}
                currency={creditNote.currency}
                emptyLabel="Not recorded on this note"
              />
            </DetailItem>
            <DetailItem label="Grand total">
              <span className="text-base font-semibold text-fg">
                <Money value={creditNote.grandTotal} currency={creditNote.currency} />
              </span>
            </DetailItem>
          </DetailGroup>

          {/*
            🔴 **REMAINING AND SPENDABLE ARE TWO FACTS AND ARE RENDERED AS TWO.**
            `remainingAmount` is reported on a DRAFT too, where it is the note's
            whole value and none of it can be allocated to anything. Folding them
            — hiding the figure unless the note is spendable, or reading a
            positive remainder as available credit — makes a draft
            indistinguishable from a note that has been fully spent, and those
            are opposite states.

            ⛔ **The two LABELS are the module's, and they are one word apart.**
            `CREDIT_AVAILABLE_LABEL` may only ever sit over a figure that passed
            `spendableOf`; `CREDIT_REMAINING_LABEL` is safe on every note in
            every state, which is exactly why it exists. Swapping them compiles,
            prints a plausible amount, and is wrong in the reassuring direction.

            ⚠️ Neither figure is derived here. `remainingAmount` is
            `grandTotal - allocatedTotal` computed exactly in the database, and a
            client repeating that subtraction disagrees by a penny on the one
            number whose entire purpose is to reconcile.
          */}
          <DetailGroup title="Against invoices">
            <DetailItem label={CREDIT_ALLOCATED_LABEL}>
              <Money value={creditNote.allocatedTotal} currency={creditNote.currency} />
            </DetailItem>
            <DetailItem label={CREDIT_REMAINING_LABEL}>
              <span className="text-base font-semibold text-fg">
                <Money value={creditNote.remainingAmount} currency={creditNote.currency} />
              </span>
            </DetailItem>
            <DetailItem label={CREDIT_AVAILABLE_LABEL}>
              {spendable ? (
                <Money value={creditNote.remainingAmount} currency={creditNote.currency} />
              ) : (
                /*
                 * ⛔ **Never `0.00` here.** A fabricated zero would read as a
                 * note that has been fully spent, which is the opposite state
                 * from one that has not been approved or has been withdrawn —
                 * and the status badge above is what says which. A value, not a
                 * sentence: the fact belongs beside the figure it qualifies.
                 */
                <span className="text-fg-subtle">Not spendable</span>
              )}
            </DetailItem>
          </DetailGroup>
        </Panel>
            ),
            lines: () => (
        <div className="grid min-w-0 gap-4">
        {/*
          ⚠️ **EXCEPTION 2 — a why-this-is-disabled sentence, not helper text.**
          A whole read-only grid with nothing saying why makes a status rule, a
          permission denial and an ordinary value indistinguishable. The wording
          is the module's; this page only decides where it goes, and it goes
          directly above the controls it explains.
        */}
        {!editVerdict.editable && (
          <p className="m-0 min-w-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
            {editVerdict.reason}
          </p>
        )}

        {/*
          ⚠️ `editable` is the SHARED answer, never this page's own reading of
          the status. `onChanged` goes back to the server because a line write
          rewrites the header totals above.
        */}
        <CreditNoteLineItemsTable
          creditNoteId={creditNote.id}
          currency={creditNote.currency}
          editable={editVerdict.editable}
          onChanged={() => void refetch()}
        />
        </div>
            ),
            allocations: () => (
        <>
        {/*
          Below the lines, because an allocation is what happens to this document
          AFTER it exists. It takes the whole record rather than an id: whether
          an allocation may be made at all is `spendable`, and how much is left
          is `remainingAmount`, and both are on the note.
        */}
        <CreditNoteAllocationsPanel
          creditNote={creditNote}
          onChanged={() => void refetch()}
          reloadSignal={allocationsPanelReload}
        />
        </>
            ),
          }}
        />
      </div>

      {/*
        ⛔ **All three are `ConfirmDialog`s naming the real consequence.** There
        is no `window.confirm` anywhere in this app, and a dialog that asks "are
        you sure?" tells somebody nothing they did not already know. Every
        sentence comes from `@/lib/credit-notes`, so this page cannot word them
        differently from anywhere else they appear.
      */}
      <ConfirmDialog
        open={pendingApprove}
        onClose={() => {
          setPendingApprove(false);
          setActionError(null);
        }}
        onConfirm={() =>
          void runAction(
            async () => {
              const response = await api.approveCreditNote(creditNote.id);
              settleFromWrite(response.data);
              setPendingApprove(false);
              /*
               * 2026-10-03: approving also approves the note in Xero and then
               * pushes its allocations. Both are reported; neither undoes the
               * approval. `?.` — an older server answers `{data}` alone.
               */
              const allocationName = (allocationId: number) => {
                const row = allocationsFor?.find((entry) => entry.id === allocationId);
                if (row === undefined || row.invoice === null) return null;
                return row.invoice.subject?.trim() || `Invoice #${row.invoice.id}`;
              };
              setXeroOutcomes({
                id: creditNote.id,
                lines: [
                  ...(response.xeroPush ? [creditNotePushOutcome(response.xeroPush, 'approve')] : []),
                  ...allocationPushesOutcome(response.allocationPushes ?? [], allocationName),
                ],
              });
              setAllocationsPanelReload((n) => n + 1);
              void loadAllocations();
            },
            /*
             * ⚠️ **Asked about `approve`, not about `update`.** The refusal has
             * to name the grant the server actually checked, or it sends
             * somebody to ask for a role that would change nothing — this is the
             * one action on the page where the two genuinely come apart.
             */
            denialReason(ability, 'approve', CREDIT_NOTE_MODULE) ??
              'Your access changed since this page loaded, so the credit note was not approved.',
          )
        }
        title={`Approve ${title}?`}
        /*
         * ⚠️ **Approving is what makes this money SPENDABLE against the
         * company's invoices** — the whole reason it is a permission the
         * Accounts set does not hold — and a reader pressing it from this screen
         * has no way to see that: the note looks the same afterwards, its figure
         * does not move, and what changes is a balance on another panel. The
         * module names the figure as well as the consequence, so nothing is
         * added beside it here.
         */
        description={
          <p className="m-0">
            {approvalConsequence(creditNote)} {CREDIT_NOTE_NOT_SENT_NOTE}
          </p>
        }
        confirmLabel="Approve credit note"
        tone="primary"
        loading={busy}
        error={actionError}
      />

      <ConfirmDialog
        open={pendingSubmit}
        onClose={() => {
          setPendingSubmit(false);
          setActionError(null);
        }}
        onConfirm={() =>
          void (async () => {
            setBusy(true);
            setActionError(null);
            try {
              await api.submitCreditNoteForApproval(creditNote.id);
              setPendingSubmit(false);
              /* Reload rather than splice: the status, the totals and the
                 allocations panel all read from the record. */
              setReloadToken((n) => n + 1);
            } catch (caught) {
              if (caught instanceof ApiError && caught.status === 403) {
                setActionError(
                  denialReason(ability, 'update', CREDIT_NOTE_MODULE) ??
                    'Your access changed since this page loaded, so the credit note was not submitted.',
                );
              } else if (caught instanceof ApiError && caught.status === 404) {
                /* Both a deleted note and a server without the submit route
                   answer 404 NOT_FOUND, so the message names both. */
                setActionError(
                  'The server answered “not found”: the credit note was deleted, or this server does not support submitting for approval yet. Nothing changed.',
                );
              } else {
                setActionError(caught instanceof Error ? caught.message : 'That didn’t work.');
              }
            } finally {
              setBusy(false);
            }
          })()
        }
        title={`Submit ${title} for approval?`}
        description={
          <p className="m-0">
            Credit note <strong className="tabular">#{creditNote.id}</strong>
            {creditNote.xeroCreditNoteNumber && (
              <>
                {' '}
                (<span className="tabular">Xero {creditNote.xeroCreditNoteNumber}</span>)
              </>
            )}{' '}
            goes to the approvers. It can no longer be edited until it is approved or voided.{' '}
            {CREDIT_NOTE_NOT_SENT_NOTE}
          </p>
        }
        confirmLabel="Submit for approval"
        tone="primary"
        loading={busy}
        error={actionError}
      />

      <ConfirmDialog
        open={pendingVoid}
        onClose={() => {
          setPendingVoid(false);
          setActionError(null);
        }}
        onConfirm={() =>
          void runAction(
            async () => {
              const response = await api.voidCreditNote(creditNote.id);
              settleFromWrite(response.data);
              setPendingVoid(false);
              // 2026-10-03: a note approved in Xero is voided there too.
              setXeroOutcomes({
                id: creditNote.id,
                lines: response.xeroPush ? [creditNotePushOutcome(response.xeroPush, 'void')] : [],
              });
            },
            denialReason(ability, 'update', CREDIT_NOTE_MODULE) ??
              'Your access changed since this page loaded, so the credit note was not voided.',
          )
        }
        title={`Void ${title}?`}
        /*
         * 🔴 **NEVER WORDED AS "un-approve", AND THE MODULE'S SENTENCE IS NOT.**
         * Voiding WITHDRAWS the credit; it does not undo the approval.
         * `isApproved` stays `true` on a voided note — approval is what
         * HAPPENED, voiding is what happened NEXT — and both flags being true is
         * the ORDINARY shape of a voided note, not a contradiction. Describing
         * this as reversing the approval would tell somebody the record is going
         * back to a state it can never return to.
         *
         * ⚠️ The confirm stays ENABLED on the allocations branch. `voidWarning`
         * already says the server will refuse it, and the module is explicit that
         * the dialog is the optimisation while the refusal is the guarantee.
         */
        description={
          <p className="m-0">
            {allocationsFor === null
              ? VOID_WITH_ALLOCATIONS_UNKNOWN
              : voidWarning(creditNote, allocationsFor)}
          </p>
        }
        confirmLabel="Void credit note"
        loading={busy}
        error={actionError}
      />

      <ConfirmDialog
        open={pendingDelete}
        onClose={() => {
          setPendingDelete(false);
          setActionError(null);
        }}
        onConfirm={() =>
          void runAction(
            async () => {
              await api.deleteCreditNote(creditNote.id);
              navigate('/credit-notes', { replace: true });
            },
            denialReason(ability, 'delete', CREDIT_NOTE_MODULE) ??
              'Your access changed since this page loaded, so the credit note was not deleted.',
          )
        }
        title={`Delete ${title}?`}
        /*
         * ⚠️ **The server refuses this while any allocation stands** — the
         * foreign key is RESTRICT — and its 409 NAMES THE COUNT. That refusal is
         * surfaced verbatim through `runAction`, because the count is the only
         * actionable part: it says how much has to be un-applied first.
         *
         * ⛔ **`deleteRefusal` returning `null` is NOT a promise the delete will
         * succeed** — it means this app has no reason to refuse it. The server
         * holds reasons this app cannot see, which is why the 409 is still
         * handled rather than treated as unreachable.
         */
        description={
          <>
            <p className="m-0">
              This deletes the credit note and every line on it. If it is in Xero, it stays there;
              only the SEED EMERGE record goes. It cannot be undone.
            </p>
            {allocationsFor === null && (
              <p className="m-0 mt-2">{DELETE_WITH_ALLOCATIONS_UNKNOWN}</p>
            )}
          </>
        }
        confirmLabel="Delete credit note"
        loading={busy}
        /*
         * The refusal this app can already see, shown before it is collected
         * rather than after. ⚠️ `confirmDisabled` is only ever set BESIDE an
         * `error` that says why — a bare disabled button makes a refusal, a
         * permission denial and an ordinary state look identical.
         */
        error={actionError ?? deleteBlocked}
        confirmDisabled={actionError === null && deleteBlocked !== null}
      />
    </>
  );
}
